import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { springs, useMotion } from '../motion';

// Floating menu at a screen position (right click, long press, "more" buttons).
// Closes on outside click/touch, Escape and scroll. Rendered into <body>, so a transformed
// ancestor (slide-in animations) can't shift the fixed position.
export function ContextMenu({ x, y, onClose, width = 224, children }) {
  const ref = useRef();

  useEffect(() => {
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside, { passive: true });
    document.addEventListener('keydown', key);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  // Keep the menu on screen; estimated height is corrected by the max() below
  const left = Math.min(Math.max(x - 20, 8), window.innerWidth - width - 8);
  const top = Math.min(y, window.innerHeight - 220);

  // Springs open from the point where it was opened, fades away when closed
  useMotion(ref, {
    enter: { y: -4, scale: 0.9, opacity: 0 },
    exit: { scale: 0.96, opacity: 0 },
    origin: `${Math.min(Math.max(x - left, 0), width)}px 0px`,
    config: springs.snappy,
  });

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className="fixed z-[500] rounded-lg border border-solid border-border bg-surface p-1.5 shadow-modal"
      style={{ top: Math.max(top, 8), left, width }}
    >
      {children}
    </div>,
    document.body
  );
}

export function MenuItem({ onClick, disabled, danger, icon, children }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-3 py-2.5 text-left font-body text-sm ${
        disabled ? 'cursor-not-allowed text-text-dim' : danger ? 'cursor-pointer text-red hover:bg-surface-2' : 'cursor-pointer text-text hover:bg-surface-2'
      }`}
    >
      {icon && <span className="flex w-4 shrink-0 justify-center">{icon}</span>}
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}

export const MenuDivider = () => <div className="mx-2 my-1 h-px bg-border" />;

/* Small inline icons used in chat menus */
const svg = (children) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
export const Icons = {
  edit: svg(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>),
  trash: svg(<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></>),
  flag: svg(<><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>),
  copy: svg(<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>),
  block: svg(<><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></>),
  clock: svg(<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>),
  users: svg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  userPlus: svg(<><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></>),
  logout: svg(<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>),
  crown: svg(<path d="M2 18l2-11 5 5 3-7 3 7 5-5 2 11z" />),
  pin: svg(<path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />),
  more: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>,
};
